import type { CsChatUnreadStatus } from "./context";

/**
 * markRead 送信後、refetch 結果で optimistic な 0 を上書きしないためのグレース期間。
 *
 * markRead は WS への fire-and-forget 送信で、サーバー側の read 書き込みは
 * background（`waitUntil`）、`read` broadcast も書き込み完了を保証しない。
 * そのため markRead 送信直後（特に `/support` 離脱時の refetch）に走る fetch は、
 * 書き込み反映前の古い `unreadCount` を返し、optimistic な 0 を上書きしてバッジを
 * 復活させ得る。この期間中は fetch 結果によらず 0 を維持し、期間経過後のトリガー
 * （focus / polling / 次の遷移）で真値に収束させる。
 */
export const MARK_READ_GRACE_MS = 10_000;

export type CsChatUnreadState = {
  status: CsChatUnreadStatus;
  channelId: string | null;
  unreadCount: number;
  /** 直近に markRead を通知された時刻(ms)。未通知は null。 */
  markReadAt: number | null;
};

export const initialCsChatUnreadState: CsChatUnreadState = {
  status: "idle",
  channelId: null,
  unreadCount: 0,
  markReadAt: null,
};

export type CsChatUnreadAction =
  | { type: "fetchStart" }
  | { type: "fetchSuccess"; channelId: string; unreadCount: number; at: number }
  | { type: "fetchError" }
  | { type: "markRead"; at: number }
  | { type: "reset" };

export const csChatUnreadReducer = (
  state: CsChatUnreadState,
  action: CsChatUnreadAction,
): CsChatUnreadState => {
  switch (action.type) {
    case "fetchStart":
      // 初回ロード済み(success)なら silent refetch とし、直前のデータを保持する。
      // polling / focus の裏更新でバッジやチャット画面を loading にちらつかせない。
      return state.status === "success"
        ? state
        : { ...state, status: "loading" };

    case "fetchSuccess": {
      const withinGrace =
        state.markReadAt !== null &&
        action.at - state.markReadAt < MARK_READ_GRACE_MS;

      return {
        status: "success",
        channelId: action.channelId,
        // グレース期間中は refetch 結果によらず 0 を維持する（必須ルール）。
        unreadCount: withinGrace ? 0 : action.unreadCount,
        markReadAt: state.markReadAt,
      };
    }

    case "fetchError":
      // 一度 success になった後の裏更新失敗では直近の未読数を維持する
      // （polling / focus の失敗でバッジやチャット画面を壊さない）。
      return state.status === "success"
        ? state
        : { ...state, status: "error", channelId: null, unreadCount: 0 };

    case "markRead":
      // 本人がチャットを見て既読化した。内部状態を 0 に確定し、
      // グレース期間の起点を更新する。
      return { ...state, unreadCount: 0, markReadAt: action.at };

    case "reset":
      return initialCsChatUnreadState;
  }
};

/**
 * バッジ表示用の未読数。`/support` 表示中は本人がチャットを見ているため常に 0。
 * それ以外では内部状態の未読数（初回 success 前・エラー時は 0、markRead 後・
 * グレース期間中は 0）をそのまま返す。
 */
export const selectDisplayUnreadCount = (
  state: CsChatUnreadState,
  isOnSupportPage: boolean,
): number => (isOnSupportPage ? 0 : state.unreadCount);
